import { useState, useMemo, useEffect } from "react";
import { format, parseISO, addMinutes } from "date-fns";
import { VenueTimeFormats, parseVenueTime } from "@/utils/timezone";
import { cn } from "@/lib/utils";
import { NavHeader } from "@/components/layout/nav-header";
import { useBookings } from "@/hooks/use-bookings";
import { useAuth } from "@/hooks/use-auth";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookingLifecycleStatus, statusConfig } from "@/types/booking";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Trash2, Edit, Instagram, Link as LinkIcon, Music, Youtube, Pencil, Eye, ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cancelBookingApi } from "../services/bookings";
import { Button } from "@/components/ui/button";
import { PhoneInput, type PhoneInputValue } from "@/components/ui/phone-input";
import { toast } from "@/hooks/use-toast";
import { useLocation, Link } from "wouter";

interface Booking {
  id: string | number;
  dj: { id: string | number; displayName?: string };
  timeSlot: {
    startTime: string | Date;
    endTime: string | Date;
  };
  status: string;
  lifecycleStatus?: BookingLifecycleStatus | null;
  performedAt?: string | Date | null;
  causeLink?: string | null;
  notes?: string;
  set?: { 
    videoUrl?: string | null;
    publicVideoUrl?: string | null;
    causeLink?: string | null;
    causeLabel?: string | null;
    artistApprovedAt?: string | Date | null;
    postedAt?: string | Date | null;
  } | null;
  createdAt?: string;
}

interface User {
  id: string | number;
  email?: string;
  fullName?: string;
  displayName?: string;
  phoneNumber?: string;
  instagramHandle?: string;
  soundCloudLink?: string;
  spotifyPlaylistLink?: string;
  mixLink?: string;
  location?: string;
  // avatarUrl is not on the user object from useAuth based on previous errors
}

export default function ProfilePage() {
  const { data: bookingsData, isLoading: bookingsLoading, error: bookingsError } = useBookings();
  const { user } = useAuth() as { user: User | null }; // Type assertion for user
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation(); // location not used, removed from state

  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showRiderModal, setShowRiderModal] = useState(false);
  const [selectedBookingRider, setSelectedBookingRider] = useState<{ booking: Booking | null }>({ booking: null });
  
  // New state for filters
  const [bookingFilter, setBookingFilter] = useState<"all" | "upcoming" | "past">("all");
  
  // Edit profile form state
  const [editFormData, setEditFormData] = useState({
    fullName: user?.fullName || '',
    displayName: user?.displayName || '',
    location: user?.location || '',
    phoneNumber: user?.phoneNumber || '',
    instagramHandle: user?.instagramHandle || '',
    soundCloudLink: user?.soundCloudLink || '',
    mixLink: user?.mixLink || '',
    spotifyPlaylistLink: user?.spotifyPlaylistLink || '',
  });

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setEditFormData({
        fullName: user.fullName || '',
        displayName: user.displayName || '',
        location: user.location || '',
        phoneNumber: user.phoneNumber || '',
        instagramHandle: user.instagramHandle || '',
        soundCloudLink: user.soundCloudLink || '',
        mixLink: user.mixLink || '',
        spotifyPlaylistLink: user.spotifyPlaylistLink || '',
      });
    }
  }, [user]);

  const now = new Date();

  const userBookings: Booking[] = bookingsData
    ?.filter((booking: Booking) => booking.dj.id === user?.id)
    .map((b: any) => ({ ...b })) || []; // Ensure it's an array of Booking

  // Filter and sort bookings based on filters
  const filteredBookings = useMemo(() => {
    let filtered = [...userBookings];
    
    // Filter for bookings only (pre-performance statuses)
    filtered = filtered.filter((booking) => {
      // Only show bookings with pre-performance lifecycle statuses
      return booking.lifecycleStatus && [
        BookingLifecycleStatus.PENDING_APPROVAL,
        BookingLifecycleStatus.SCHEDULED,
        BookingLifecycleStatus.REJECTED,
        BookingLifecycleStatus.CANCELLED,
        BookingLifecycleStatus.NO_SHOW
      ].includes(booking.lifecycleStatus);
    });
    
    // Apply time filter
    if (bookingFilter === "upcoming") {
      filtered = filtered.filter((booking) => parseVenueTime(booking.timeSlot.startTime) >= now);
    } else if (bookingFilter === "past") {
      filtered = filtered.filter((booking) => parseVenueTime(booking.timeSlot.startTime) < now);
    }
    
    // Sort newest first
    filtered.sort((a, b) => {
      const dateA = parseVenueTime(a.timeSlot.startTime).getTime();
      const dateB = parseVenueTime(b.timeSlot.startTime).getTime();
      return dateB - dateA;
    });
    
    return filtered;
  }, [userBookings, bookingFilter, now]);

  // Helper function to get booking status label
  const getBookingStatus = (booking: Booking) => {
    // Always use lifecycle status when available
    if (booking.lifecycleStatus) {
      const config = statusConfig[booking.lifecycleStatus];
      if (config) return config;
    }
    
    // This shouldn't happen if lifecycleStatus is properly set
    return { label: "Unknown", color: "bg-gray-500" };
  };

  // Helper function for recording status
  const getRecordingStatus = (booking: Booking) => {
    // Always use lifecycle status when available
    if (booking.lifecycleStatus) {
      const config = statusConfig[booking.lifecycleStatus];
      if (config) return config;
    }
    
    // This shouldn't happen if lifecycleStatus is properly set
    return { label: "Unknown", color: "bg-gray-500" };
  };

  
  // Get recordings (video-related statuses), sorted newest first
  const recordings = useMemo(() => {
    return userBookings
      .filter(booking =>
        booking.lifecycleStatus && [
          BookingLifecycleStatus.AWAITING_VIDEO,
          BookingLifecycleStatus.ARTIST_REVIEW,
          BookingLifecycleStatus.READY_TO_POST,
          BookingLifecycleStatus.POSTED
        ].includes(booking.lifecycleStatus)
      )
      .sort((a, b) => {
        const dateA = parseVenueTime(a.timeSlot.startTime).getTime();
        const dateB = parseVenueTime(b.timeSlot.startTime).getTime();
        return dateB - dateA;
      });
  }, [userBookings]);

  const cancelBooking = useMutation({
    mutationFn: (bookingId: string | number) => cancelBookingApi(bookingId.toString()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toast({
        title: "Booking Cancelled",
        // Cascade: If 'showNotification' error persists, check implementation of '@/hooks/use-toast.ts'
        description: "Your booking has been cancelled successfully"
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel booking",
        variant: "destructive"
      });
      console.error("Cancel booking error:", error);
    }
  });

  const handleUpdateClick = (booking: Booking) => {
    // Navigate to the edit booking page
    setLocation(`/booking/${booking.id}/edit`);
  };

  // Helper function to ensure URL has https://
  const ensureHttps = (url: string): string => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const handleViewRider = (booking: Booking) => {
    setSelectedBookingRider({ booking });
    setShowRiderModal(true);
  };

  if (bookingsLoading) return <div className="container mx-auto px-4 py-8 text-center">Loading bookings...</div>;
  if (bookingsError) return <div className="container mx-auto px-4 py-8 text-center text-red-500">Error loading bookings.</div>;

  if (showEditProfile) {
    return (
      <div className="min-h-screen bg-clubhouse-black text-white">
        <NavHeader />
        <main className="max-w-2xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => setShowEditProfile(false)}
              className="h-8 w-8 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h1 className="text-[28px] font-semibold tracking-tight">Edit profile</h1>
          </div>

          <div>
            <div className="space-y-10">
              {/* Personal Info Section */}
              <div>
                <h2 className="text-[18px] font-semibold mb-1">Personal Info</h2>
                <p className="text-[14px] text-white/30 mb-6">Tell us a bit about yourself</p>
                <div className="space-y-5">
                  <div>
                    <label className="block text-[13px] text-white/50 mb-2">Full name</label>
                    <input
                      type="text"
                      value={editFormData.fullName}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, fullName: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[14px] text-white placeholder:text-white/25 focus:outline-none focus:border-white/30 transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] text-white/50 mb-2">Display name</label>
                    <input
                      type="text"
                      value={editFormData.displayName}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, displayName: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[14px] text-white placeholder:text-white/25 focus:outline-none focus:border-white/30 transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] text-white/50 mb-2">Location</label>
                    <input
                      type="text"
                      value={editFormData.location}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[14px] text-white placeholder:text-white/25 focus:outline-none focus:border-white/30 transition-all duration-200"
                      placeholder="e.g. Los Angeles, CA"
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] text-white/50 mb-2">Phone number</label>
                    <PhoneInput
                      value={editFormData.phoneNumber}
                      onChange={(value) => setEditFormData(prev => ({ ...prev, phoneNumber: value }))}
                      placeholder="Enter phone number"
                      defaultCountry="us"
                    />
                    <p className="text-[12px] text-white/25 mt-1">So we can contact you once we confirm a time.</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/[0.08]"></div>

              {/* Portfolio Links Section */}
              <div>
                <h2 className="text-[18px] font-semibold mb-1">Portfolio Links</h2>
                <p className="text-[14px] text-white/30 mb-6">Give us some insight into what your sound is</p>
                <div className="space-y-5">
                  <div>
                    <label className="block text-[13px] text-white/50 mb-2">Instagram handle</label>
                    <input
                      type="text"
                      value={editFormData.instagramHandle}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, instagramHandle: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[14px] text-white placeholder:text-white/25 focus:outline-none focus:border-white/30 transition-all duration-200"
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] text-white/50 mb-2">SoundCloud link</label>
                    <input
                      type="url"
                      value={editFormData.soundCloudLink}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, soundCloudLink: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[14px] text-white placeholder:text-white/25 focus:outline-none focus:border-white/30 transition-all duration-200"
                      placeholder="https://soundcloud.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] text-white/50 mb-2">Mix link</label>
                    <input
                      type="url"
                      value={editFormData.mixLink}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, mixLink: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[14px] text-white placeholder:text-white/25 focus:outline-none focus:border-white/30 transition-all duration-200"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] text-white/50 mb-2">Spotify playlist link</label>
                    <input
                      type="url"
                      value={editFormData.spotifyPlaylistLink}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, spotifyPlaylistLink: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[14px] text-white placeholder:text-white/25 focus:outline-none focus:border-white/30 transition-all duration-200"
                      placeholder="https://open.spotify.com/..."
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <Button
                className="bg-white hover:bg-white/90 text-black"
                size="sm"
                onClick={async () => {
                  try {
                    const response = await fetch('/api/user/update', {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(editFormData),
                      credentials: 'include',
                    });

                    if (!response.ok) {
                      throw new Error('Failed to update profile');
                    }

                    toast({
                      title: "Profile updated",
                      description: "Your profile has been updated successfully.",
                    });

                    queryClient.invalidateQueries({ queryKey: ['/api/user'] });
                    setShowEditProfile(false);
                  } catch (error) {
                    console.error('Error updating profile:', error);
                    toast({
                      title: "Error",
                      description: "Failed to update profile. Please try again.",
                      variant: "destructive",
                    });
                  }
                }}
              >
                Save changes
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div id="my-bookings-page-wrapper" tabIndex={-1} className="min-h-screen bg-clubhouse-black text-white outline-none">
      <NavHeader />
      
      {/* Single Tabs component wrapping everything */}
      <Tabs defaultValue="bookings" className="w-full">
        {/* Profile Header with Tab Triggers */}
        <div className="bg-clubhouse-black border-b border-white/[0.08]">
          <div className="container mx-auto px-4 pt-8">
            {/* Compact profile row */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <h1 className="text-[28px] font-semibold tracking-tight truncate">{user?.displayName || user?.fullName || 'Artist Name'}</h1>
                <button
                  onClick={() => {
                    setShowEditProfile(true);
                    setEditFormData({
                      fullName: user?.fullName || '',
                      displayName: user?.displayName || '',
                      location: user?.location || '',
                      phoneNumber: user?.phoneNumber || '',
                      instagramHandle: user?.instagramHandle || '',
                      soundCloudLink: user?.soundCloudLink || '',
                      mixLink: user?.mixLink || '',
                      spotifyPlaylistLink: user?.spotifyPlaylistLink || '',
                    });
                  }}
                  className="h-7 w-7 rounded-full flex items-center justify-center text-white/25 hover:text-white/60 hover:bg-white/[0.06] transition-colors shrink-0"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Metadata + social icons inline */}
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-[14px] text-white/30">
                  {[user?.email, user?.location].filter(Boolean).join(' \u00B7 ')}
                </span>
                {(user?.instagramHandle || user?.soundCloudLink || user?.mixLink || user?.spotifyPlaylistLink) && (
                  <>
                    <span className="text-white/10">|</span>
                    <div className="flex items-center gap-0.5">
                      {user?.instagramHandle && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={`https://instagram.com/${user.instagramHandle.replace('@', '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-7 w-7 rounded-full flex items-center justify-center text-white/25 hover:text-white/60 hover:bg-white/[0.06] transition-colors"
                            >
                              <Instagram className="h-3.5 w-3.5" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent><p>Instagram</p></TooltipContent>
                        </Tooltip>
                      )}
                      {user?.soundCloudLink && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={user.soundCloudLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-7 w-7 rounded-full flex items-center justify-center text-white/25 hover:text-white/60 hover:bg-white/[0.06] transition-colors"
                            >
                              <Music className="h-3.5 w-3.5" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent><p>SoundCloud</p></TooltipContent>
                        </Tooltip>
                      )}
                      {user?.mixLink && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={user.mixLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-7 w-7 rounded-full flex items-center justify-center text-white/25 hover:text-white/60 hover:bg-white/[0.06] transition-colors"
                            >
                              <LinkIcon className="h-3.5 w-3.5" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent><p>Mix</p></TooltipContent>
                        </Tooltip>
                      )}
                      {user?.spotifyPlaylistLink && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={user.spotifyPlaylistLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-7 w-7 rounded-full flex items-center justify-center text-white/25 hover:text-white/60 hover:bg-white/[0.06] transition-colors"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                              </svg>
                            </a>
                          </TooltipTrigger>
                          <TooltipContent><p>Spotify</p></TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Tab Triggers */}
            <TabsList className="bg-transparent p-0 h-auto w-full border-0 grid grid-cols-2 sm:flex sm:gap-8 sm:justify-start">
              <TabsTrigger
                value="bookings"
                className="bg-transparent px-0 pb-3 pt-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-white rounded-none text-[14px] font-medium text-white/40 data-[state=active]:text-white transition-colors"
              >
                Bookings
              </TabsTrigger>
              <TabsTrigger
                value="recordings"
                className="bg-transparent px-0 pb-3 pt-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-white rounded-none text-[14px] font-medium text-white/40 data-[state=active]:text-white transition-colors"
              >
                Recordings ({recordings.length})
              </TabsTrigger>
            </TabsList>
          </div>
        </div>
        
        {/* Main Content with Tab Contents */}
        <main className="container mx-auto px-4 py-8 animate-page-in">
          {/* Bookings Tab Content */}
          <TabsContent value="bookings" className="mt-0">
            {/* Minimal text filters */}
            <div className="flex items-center gap-6 mb-6">
              {(["all", "upcoming", "past"] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setBookingFilter(filter)}
                  className={cn(
                    "text-[14px] transition-colors capitalize",
                    bookingFilter === filter
                      ? "text-white font-medium"
                      : "text-white/30 hover:text-white/60"
                  )}
                >
                  {filter}
                </button>
              ))}
            </div>

            {/* Bookings — Mobile cards / Desktop table */}

            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {filteredBookings.map((booking) => {
                const status = getBookingStatus(booking);
                return (
                  <div key={booking.id} className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-[14px] text-white/80">
                          {VenueTimeFormats.dateOnly(booking.timeSlot.startTime)}
                        </div>
                        <div className="text-[13px] text-white/30 mt-0.5">
                          {VenueTimeFormats.timeOnly(booking.timeSlot.startTime)} –{' '}
                          {VenueTimeFormats.timeOnly(booking.timeSlot.endTime)}
                        </div>
                      </div>
                      <Badge className={cn("border-0 text-[12px]", status.color)}>
                        {status.label}
                      </Badge>
                    </div>
                    {booking.status !== "rejected" && booking.status !== "completed" && parseVenueTime(booking.timeSlot.startTime) >= now && (
                      <div className="flex items-center justify-end gap-4 mt-3 pt-3 border-t border-white/[0.06]">
                        <button
                          onClick={() => handleUpdateClick(booking)}
                          disabled={cancelBooking.isPending}
                          className="text-[13px] text-white/40 hover:text-white/60 transition-colors disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="text-[13px] text-white/40 hover:text-white/60 transition-colors">
                              Cancel
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel booking?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. This will permanently cancel your booking.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] text-white">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => cancelBooking.mutate(booking.id)}
                                className="bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25"
                              >
                                Cancel booking
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredBookings.length === 0 && (
                <div className="p-12 text-center text-white/25 text-[14px]">
                  No bookings found.
                </div>
              )}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/[0.08] hover:bg-transparent">
                    <TableHead className="text-white/30 text-[13px] font-normal pl-6 w-[45%]">Date & Time</TableHead>
                    <TableHead className="text-white/30 text-[13px] font-normal w-[35%]">Status</TableHead>
                    <TableHead className="text-white/30 text-[13px] font-normal text-right pr-6 w-[20%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking) => {
                    const status = getBookingStatus(booking);

                    return (
                      <TableRow key={booking.id} className="border-white/[0.06]">
                        <TableCell className="pl-6 py-4">
                          <div>
                            <div className="text-[14px] text-white/80">
                              {VenueTimeFormats.dateOnly(booking.timeSlot.startTime)}
                            </div>
                            <div className="text-[13px] text-white/30 mt-0.5">
                              {VenueTimeFormats.timeOnly(booking.timeSlot.startTime)} –{' '}
                              {VenueTimeFormats.timeOnly(booking.timeSlot.endTime)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-4">
                          <Badge className={cn("border-0 text-[12px]", status.color)}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right pr-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            {booking.status !== "rejected" && booking.status !== "completed" && parseVenueTime(booking.timeSlot.startTime) >= now && (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => handleUpdateClick(booking)}
                                      disabled={cancelBooking.isPending}
                                      className="h-7 w-7 rounded-full flex items-center justify-center text-violet-400 hover:text-violet-300 hover:bg-white/[0.06] transition-colors disabled:opacity-50"
                                    >
                                      <Edit className="h-3.5 w-3.5" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Edit</p></TooltipContent>
                                </Tooltip>
                                <AlertDialog>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <AlertDialogTrigger asChild>
                                        <button className="h-7 w-7 rounded-full flex items-center justify-center text-rose-400/70 hover:text-rose-400 hover:bg-white/[0.06] transition-colors">
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      </AlertDialogTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent><p>Cancel</p></TooltipContent>
                                  </Tooltip>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Cancel booking?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This action cannot be undone. This will permanently cancel your booking.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] text-white">Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => cancelBooking.mutate(booking.id)}
                                        className="bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25"
                                      >
                                        Cancel booking
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filteredBookings.length === 0 && (
                <div className="p-12 text-center text-white/25 text-[14px]">
                  No bookings found.
                </div>
              )}
            </div>
          </TabsContent>
          
          {/* Recordings Tab Content */}
          <TabsContent value="recordings" className="mt-0">
            {/* Recordings — Mobile cards / Desktop table */}

            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {recordings.map((booking) => {
                const recordingStatus = getRecordingStatus(booking);
                if (!recordingStatus) return null;
                return (
                  <div
                    key={booking.id}
                    className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-4 cursor-pointer active:bg-white/[0.06] transition-colors"
                    onClick={() => setLocation(`/review/${booking.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-[14px] text-white/80">
                          {VenueTimeFormats.dateOnly(booking.timeSlot.startTime)}
                        </div>
                        <div className="text-[13px] text-white/30 mt-0.5">
                          {VenueTimeFormats.timeOnly(booking.timeSlot.startTime)} –{' '}
                          {VenueTimeFormats.timeOnly(booking.timeSlot.endTime)}
                        </div>
                      </div>
                      <Badge className={cn("border-0 text-[12px]", recordingStatus.color)}>
                        {recordingStatus.label}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.06]">
                      <span className="flex items-center gap-1.5 text-[13px] text-white/50">
                        {recordingStatus.label === "Artist Review" ? "Review video" : "View details"}
                        <ChevronRight className="h-3.5 w-3.5" />
                      </span>
                      {booking.set?.publicVideoUrl && (
                        <a
                          href={booking.set.publicVideoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-[13px] text-white/30 hover:text-white/50 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Youtube className="h-3.5 w-3.5" />
                          YouTube
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
              {recordings.length === 0 && (
                <div className="p-12 text-center text-white/25 text-[14px]">
                  No recordings yet.
                </div>
              )}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/[0.08] hover:bg-transparent">
                    <TableHead className="text-white/30 text-[13px] font-normal pl-6 w-[45%]">Date & Time</TableHead>
                    <TableHead className="text-white/30 text-[13px] font-normal w-[35%]">Status</TableHead>
                    <TableHead className="text-white/30 text-[13px] font-normal text-right pr-6 w-[20%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recordings.map((booking) => {
                    const recordingStatus = getRecordingStatus(booking);
                    if (!recordingStatus) return null;

                    return (
                      <TableRow
                        key={booking.id}
                        className="border-white/[0.06] cursor-pointer hover:bg-white/[0.03] transition-colors"
                        onClick={() => setLocation(`/review/${booking.id}`)}
                      >
                        <TableCell className="pl-6 py-4">
                          <div>
                            <div className="text-[14px] text-white/80">
                              {VenueTimeFormats.dateOnly(booking.timeSlot.startTime)}
                            </div>
                            <div className="text-[13px] text-white/30 mt-0.5">
                              {VenueTimeFormats.timeOnly(booking.timeSlot.startTime)} –{' '}
                              {VenueTimeFormats.timeOnly(booking.timeSlot.endTime)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-4">
                          <Badge className={cn("border-0 text-[12px]", recordingStatus.color)}>
                            {recordingStatus.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right pr-6 py-4">
                          <div className="flex items-center justify-end gap-4">
                            {booking.set?.publicVideoUrl && (
                              <a
                                href={booking.set.publicVideoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 text-[13px] text-white/30 hover:text-white/50 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Youtube className="h-3.5 w-3.5" />
                                YouTube
                              </a>
                            )}
                            <span className="flex items-center gap-1.5 text-[13px] text-white/50">
                              {recordingStatus.label === "Artist Review" ? "Review video" : "View details"}
                              <ChevronRight className="h-3.5 w-3.5" />
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {recordings.length === 0 && (
                <div className="p-12 text-center text-white/25 text-[14px]">
                  No recordings yet.
                </div>
              )}
            </div>
          </TabsContent>
        </main>
      </Tabs>
      
      {/* Rider Modal */}
      <Dialog open={showRiderModal} onOpenChange={setShowRiderModal}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Booking details</DialogTitle>
            <DialogDescription>
              {selectedBookingRider.booking && (
                <span>
                  {VenueTimeFormats.dateOnly(selectedBookingRider.booking.timeSlot.startTime)} \u00B7{' '}
                  {VenueTimeFormats.timeOnly(selectedBookingRider.booking.timeSlot.startTime)} –{' '}
                  {VenueTimeFormats.timeOnly(selectedBookingRider.booking.timeSlot.endTime)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-[13px] text-white/30 mb-2">Notes</p>
            <div className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-4">
              <p className="text-[14px] text-white/70 whitespace-pre-wrap leading-relaxed">
                {selectedBookingRider.booking?.notes || 'No notes provided.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowRiderModal(false)}
              className="text-white/40 hover:text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}